import { useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import { VoiceTranscript } from "@/components/voice/VoiceTranscript";
import type { VoiceTurn } from "@/lib/voice/conversation";
import { useVoiceAssistant } from "../VoiceAssistantContext";
import { dictationBusy, type SpeechTurn } from "../speech/controller";
import { speechTextReason } from "@/lib/assistant/speech-contract";
import type { DirectTextTurn } from "../hooks/use-direct-text";
import { AssistantBusinessReceipts } from "./AssistantBusinessReceipt";
import { readableSpeechText } from "../speech-presentation";

const SLOW_TURN_MS = 10_000;

function isNonEmptyString(value: unknown): value is string {
  return typeof value === "string" && value.length > 0;
}

function speechTurn(value: VoiceTurn): SpeechTurn | null {
  const candidate = value as Partial<SpeechTurn>;
  return isNonEmptyString(candidate.conversationId) && isNonEmptyString(candidate.messageEnvelope)
    ? (candidate as SpeechTurn)
    : null;
}

function readAloudStatus({
  selected,
  speech,
}: {
  selected: boolean;
  speech: ReturnType<typeof useVoiceAssistant>["speech"];
}): string {
  if (!selected || !speech) return "";
  switch (speech.state.readAloud) {
    case "loading":
      return "Generating speech…";
    case "playing":
      return "Playing…";
    case "completed":
      return "Playback complete";
    default:
      return speech.state.readError ?? "";
  }
}

function ReadAloudAction({ raw }: { raw: VoiceTurn }) {
  const session = useVoiceAssistant();
  const speech = session.speech;
  const turn = speechTurn(raw);
  const mayRead =
    turn &&
    speech?.preferences.readAloud &&
    speech.capabilities.readAloud.available &&
    session.mode === "text" &&
    turn.role === "assistant" &&
    turn.conversationId === session.textConversationId;

  if (!mayRead || !turn || !speech) return null;

  const reason = speechTextReason(turn.text, turn.isFinal);
  const selected = speech.state.messageId === turn.id;
  const active = selected && ["loading", "playing"].includes(speech.state.readAloud);
  const label = active
    ? "Stop Read aloud"
    : selected && speech.state.readAloud === "blocked"
      ? "Play"
      : selected && speech.state.readAloud === "error"
        ? "Retry Read aloud"
        : "Read aloud";

  return (
    <div className="pt-2">
      <Button
        type="button"
        variant="ghost"
        disabled={Boolean(reason) || dictationBusy(speech.state)}
        onClick={() => {
          if (active) speech.stopReadAloud();
          else void speech.read(turn);
        }}
      >
        {label}
      </Button>
      <span aria-live="polite" className="text-xs">
        {reason ?? readAloudStatus({ selected, speech })}
      </span>
    </div>
  );
}

/**
 * The hosted functions proxy buffers a reply until it is complete, so a slow
 * turn shows no partial text. After ten seconds, show that it is still running.
 */
function PendingElapsed({ startedAt }: { startedAt: number }) {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const timer = setInterval(() => setNow(Date.now()), 1_000);
    return () => clearInterval(timer);
  }, []);
  const elapsed = now - startedAt;
  if (elapsed < SLOW_TURN_MS) return null;
  return (
    <p aria-live="off" className="pt-1 text-muted-foreground text-xs tabular-nums">
      Still working · {Math.floor(elapsed / 1_000)}s
    </p>
  );
}

function TurnActions({ raw }: { raw: VoiceTurn }) {
  const turn = raw as DirectTextTurn;
  return (
    <>
      {!turn.isFinal && turn.startedAt !== undefined && (
        <PendingElapsed startedAt={turn.startedAt} />
      )}
      <AssistantBusinessReceipts receipts={turn.receipts} reconciliation={turn.reconciliation} />
      <ReadAloudAction raw={raw} />
    </>
  );
}

export function AssistantTranscript({
  className,
  turns,
}: {
  className?: string;
  turns: VoiceTurn[];
}) {
  const { spokenCaption } = useVoiceAssistant();
  return (
    <>
      {spokenCaption && (
        <p
          aria-label="Spoken caption"
          aria-live="off"
          className="pb-2 text-sm text-muted-foreground"
        >
          {spokenCaption.text.slice(0, spokenCaption.activeStart)}
          <mark className="bg-accent text-accent-foreground rounded-sm">
            {spokenCaption.text.slice(spokenCaption.activeStart, spokenCaption.activeEnd)}
          </mark>
          {spokenCaption.text.slice(spokenCaption.activeEnd)}
        </p>
      )}
      <VoiceTranscript
        className={className}
        turns={turns}
        displayText={(turn) =>
          turn.channel === "voice" ? readableSpeechText(turn.text) : turn.text
        }
        renderActions={(turn) => <TurnActions raw={turn} />}
      />
    </>
  );
}
